import type { ReactElement } from 'react'
import { Button as HeadlessButton } from '@headlessui/react'

export interface ButtonProps {
  label: string
  labelStyle?: string
  onClick: () => void
  variant?: 'cta' | 'highlight' | 'alt' | 'success' | 'danger'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  icon?: ReactElement | null
  disabled?: boolean
  fullwidth?: boolean
}

export default function Button({ label, labelStyle, onClick, variant, size, icon, disabled, fullwidth }: ButtonProps) {
  let style =
    'rounded-md text-center font-sans font-regular shadow-sm shadow-neutral-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 flex items-center justify-center gap-2'

  if (disabled) {
    style += ' cursor-not-allowed !bg-gray-400 !text-gray-200'
    switch (variant) {
      case 'cta':
        style += ' bg-cta text-cta-txt border-cta-brd'
        break
      case 'highlight':
        style += ' bg-highlight text-black'
        break
      case 'success':
        style += ' bg-success text-white'
        break
      case 'danger':
        style += ' bg-error text-white'
        break
      case 'alt':
      default:
        style += ' bg-alt text-black'
        break
    }
  } else {
    style += ' hover:cursor-pointer'
    switch (variant) {
      case 'cta':
        style +=
          ' bg-cta text-cta-txt border-cta-brd hover:bg-cta-active hover:border-cta-brd hover:text-cta-txt focus-visible:outline-cta-brd focus:ring-cta-brd'
        break
      case 'highlight':
        style += ' bg-highlight text-black hover:bg-highlight/70 focus-visible:outline-highlight focus:ring-highlight'
        break
      case 'success':
        style += ' bg-success text-white hover:bg-success-active focus-visible:outline-success focus:ring-success'
        break
      case 'danger':
        style += ' bg-error text-white hover:bg-error/70 focus-visible:outline-error focus:ring-error'
        break
      case 'alt':
      default:
        style += ' bg-alt text-black hover:bg-gray-200 focus-visible:outline-gray-400 focus:ring-gray-400'
        break
    }
  }

  switch (size) {
    case 'xs':
      style += ' px-2 py-1 text-xs my-auto font-light' //' w-16 text-xs px-1 font-regular'
      break
    case 'sm':
      style += ' px-2 !py-1 text-sm my-auto font-medium' //' w-20 text-sm px-1 font-regular'
      break
    case 'md':
      style += ' px-3 py-1 text-md font-semibold my-auto' //' w-28 text-md px-2'
      break
    case 'lg':
      style += ' px-4 py-1 text-lg font-semibold' // ' w-40 text-lg'
      break
    case 'xl':
      style += ' px-5 py-3 text-lg font-semibold' // ' w-40 text-lg'
      break
    default:
      style += ' px-3.5 py-3 text-md font-semibold'
  }

  if (fullwidth) {
    style += ' w-full'
  }

  return (
    <HeadlessButton onClick={onClick} className={style} disabled={disabled}>
      <span className={labelStyle}>{label}</span>
      {icon}
    </HeadlessButton>
  )
}
